// A live example of the affiliations / awards band for the Style pivot.
//
// It mirrors StorefrontHomePageEditor.renderAffiliationsBand exactly: the same builders, the same
// label placement, the same 48px/contain image fallback when the image dict sets nothing. The one
// deliberate difference is the band background: the canvas paints it full-bleed across the whole
// preview (fullBleedBandStyle's box-shadow trick), while here the band IS the swatch, so a plain
// elementStyleFrom is what belongs.
//
// The logos are two hardcoded inline SVGs: the example has to paint instantly and identically for
// every storefront, and must never leak one storefront's uploaded artwork into a styling surface.
// The label and description use the merchant's own text when they've written some, since those are
// the strings whose styling is being judged.

import React from 'react';
import { IAffiliations } from '../homePageConfig';
import { elementStyleFrom, textStyleFrom } from '../styleControls';
import StyleExampleFrame from '../StyleExampleFrame';

const svgUri = (svg: string) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

// An award rosette and a certification shield: flat shapes, muted neutrals plus one accent, so the
// pair reads as "partner logos" without competing with the styling being previewed.
const SAMPLE_LOGOS = [
    svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 48">
  <circle cx="24" cy="20" r="14" fill="#eef3f9" stroke="#0f6cbd" stroke-width="2.5"/>
  <path d="M24 12l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2-4.5-4.4 6.2-.9z" fill="#0f6cbd" opacity="0.8"/>
  <rect x="46" y="12" width="42" height="6" rx="3" fill="#605e5c"/>
  <rect x="46" y="24" width="30" height="5" rx="2.5" fill="#a19f9d"/>
</svg>`),
    svgUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 48">
  <path d="M20 6l14 5v12c0 8.5-5.8 15-14 19-8.2-4-14-10.5-14-19V11z" fill="#eef3f9" stroke="#107c41" stroke-width="2.5"/>
  <path d="M13 22l5 5 9-10" fill="none" stroke="#107c41" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
  <rect x="44" y="12" width="44" height="6" rx="3" fill="#605e5c"/>
  <rect x="44" y="24" width="26" height="5" rx="2.5" fill="#a19f9d"/>
</svg>`),
];

const SAMPLE_LABEL = 'Proud Member Of';
const SAMPLE_DESCRIPTION = 'The industry groups and certifications we work with every day.';

// Same flex mapping the band uses (navPodJustify), inlined for the two values that matter in a
// swatch this narrow.
const justifyFor = (align?: IAffiliations['align']): string => {
    switch (align) {
        case 'left': return 'flex-start';
        case 'right': return 'flex-end';
        case 'spaceBetween': return 'space-between';
        default: return 'center';
    }
};

const AffiliationsExample: React.FC<{ value?: IAffiliations; isDark?: boolean }> = ({ value, isDark }) => {
    const imageStyleSet = elementStyleFrom(value?.image);
    const imageStyle: React.CSSProperties = Object.keys(imageStyleSet).length
        ? imageStyleSet
        : { height: 48, width: 'auto', objectFit: 'contain' };
    const podStyle = elementStyleFrom(value?.pod);
    const labelPosition = value?.labelPosition ?? 'top';
    // The description only exists on the live band when the merchant wrote one; it also has to
    // appear the moment they start styling it, otherwise the DESCRIPTION group edits nothing
    // visible.
    const showDescription = !!value?.description || !!Object.keys(value?.descriptionStyle ?? {}).length;

    const labelNode = labelPosition !== 'none' ? (
        <div>
            <div style={textStyleFrom(value?.labelStyle)}>{value?.label || SAMPLE_LABEL}</div>
            {showDescription && (
                <div style={textStyleFrom(value?.descriptionStyle)}>{value?.description || SAMPLE_DESCRIPTION}</div>
            )}
        </div>
    ) : null;

    return (
        <StyleExampleFrame isDark={isDark} caption='Example band, updates as you edit'>
            <div style={{ ...elementStyleFrom(value?.band), width: '100%', boxSizing: 'border-box' }}>
                <div style={{
                    display: 'flex',
                    flexDirection: labelPosition === 'left' ? 'row' : 'column',
                    alignItems: labelPosition === 'left' ? 'center' : undefined,
                    gap: 12,
                }}>
                    {labelPosition !== 'bottom' && labelNode}
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: justifyFor(value?.align), gap: value?.gap ?? 24 }}>
                        {SAMPLE_LOGOS.map((logo, i) => (
                            <div key={i} style={podStyle}>
                                <img src={logo} alt='' style={imageStyle} />
                            </div>
                        ))}
                    </div>
                    {labelPosition === 'bottom' && labelNode}
                </div>
            </div>
        </StyleExampleFrame>
    );
};

export default AffiliationsExample;
